import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiPencil, HiTrash } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";

interface Voucher {
  id: number;
  code: string;
  voucherType: number;
  value: number;
  numberOfUse: number;
  maxAmount: number;
  from: string;
  to: string;
  isActive: boolean;
  isPublic: boolean;
  customers: string;
  isUsed: boolean;
}

export const VoucherIndex: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, i18n } = useTranslation('vouchers');
  const isRTL = i18n.language === 'ar';

  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAutoRefund, setIsAutoRefund] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    });

    return params.toString();
  };

  const fetchVouchers = async (page: number, size: number) => {
    setLoading(true);
    try {
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/voucher?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('messages.fetchError'));
        return;
      }

      setVouchers(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error: any) {
      showErrorToast(t('messages.fetchError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRefundVouchers = async (page: number, size: number) => {
    setLoading(true);
    try {
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/voucher/refunds?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('messages.fetchError'));
        return;
      }

      setVouchers(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error: any) {
      showErrorToast(t('messages.fetchError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAutoRefund) {
      fetchRefundVouchers(currentPage, pageSize);
      return;
    }
    fetchVouchers(currentPage, pageSize);
  }, [token, currentPage, pageSize, isAutoRefund, filters]);

  const handleCheckboxChange = (id: number) => {
    setSelectedIds((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((selectedId) => selectedId !== id)
        : [...prevSelected, id]
    );
  };

  const deleteVoucher = async (id: number) => {
    if (!window.confirm(t('messages.deleteConfirm')))
      return;

    try {
      const response = await adminApi.call(`/voucher/${id}`, 'DELETE');

      if (!response.success) {
        showErrorToast(response.error || t('messages.deleteError'));
        return;
      }

      showSuccessToast(t('messages.deleteSuccess'));
      setSelectedIds((prevSelected) =>
        prevSelected.filter((selectedId) => selectedId !== id)
      );
      if (isAutoRefund) {
        fetchRefundVouchers(currentPage, pageSize);
      } else {
        fetchVouchers(currentPage, pageSize);
      }
    } catch (error: any) {
      showErrorToast(t('messages.deleteError') + ": " + error.message);
    }
  };

  const handleCreateVoucher = () => {
    navigate('/vouchers/create');
  };

  const handleEditVoucher = (id: number) => {
    navigate(`/vouchers/edit/${id}`);
  };

  const columns: Column[] = [
    {
      key: 'select',
      title: t('table.select'),
      sortable: false,
      width: '60px',
      render: (_, voucher) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(voucher.id)}
          onChange={() => handleCheckboxChange(voucher.id)}
        />
      ),
    },
    {
      key: 'code',
      title: t('table.code'),
      sortable: true,
      filterable: true,
      filterKey: 'code',
    },
    {
      key: 'voucherType',
      title: t('table.couponType'),
      sortable: true,
      render: (value) => value === 0 ? t('types.percent') : t('types.amount'),
    },
    {
      key: 'value',
      title: t('table.value'),
      sortable: true,
    },
    {
      key: 'numberOfUse',
      title: t('table.numberOfUse'),
      sortable: true,
    },
    {
      key: 'maxAmount',
      title: t('table.maxAmount'),
      sortable: true,
      render: (value) => value === -1 ? t('status.unlimited') : value,
    },
    {
      key: 'from',
      title: t('table.from'),
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      key: 'to',
      title: t('table.to'),
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      key: 'isActive',
      title: t('table.isActive'),
      sortable: true,
      render: (value) => value ? t('status.active') : t('status.inactive'),
    },
    {
      key: 'isPublic',
      title: t('table.isPublic'),
      sortable: true,
      render: (value) => value ? t('status.active') : t('status.inactive'),
    },
    {
      key: 'isUsed',
      title: t('table.isUsed'),
      sortable: true,
      render: (value) => value ? t('status.active') : t('status.inactive'),
    },
    {
      key: 'actions',
      title: t('table.actions'),
      sortable: false,
      width: '150px',
      align: 'center',
      render: (_, voucher) => (
        <div className="action-buttons">
          <ActionButton
            variant="warning"
            size="sm"
            icon={HiPencil}
            onClick={() => handleEditVoucher(voucher.id)}
            tooltip={t('actions.edit')}
          />
          <ActionButton
            variant="danger"
            size="sm"
            icon={HiTrash}
            onClick={() => deleteVoucher(voucher.id)}
            tooltip={t('actions.delete')}
          />
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    { label: t('title') }
  ];

  const headerActions = (
    <div className="header-actions">
      <ActionButton
        variant={!isAutoRefund ? "primary" : "outline-primary"}
        onClick={() => {
          setIsAutoRefund(false);
          setCurrentPage(1);
        }}
        className="me-2"
      >
        {t('actions.coupons')}
      </ActionButton>
      <ActionButton
        variant={isAutoRefund ? "warning" : "outline-warning"}
        onClick={() => {
          setIsAutoRefund(true);
          setCurrentPage(1);
        }}
      >
        {t('actions.autoRefundCoupons')}
      </ActionButton>
    </div>
  );

  return (
    <PageLayout
      title={t('title')}
      subtitle={t('subtitle')}
      breadcrumbs={breadcrumbs}
      headerActions={headerActions}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={vouchers}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={true}
        refreshable={true}
        addButton={!isAutoRefund && hasPermission("Create Voucher") ? {
          label: t('actions.addNewCoupon'),
          onClick: handleCreateVoucher
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onRefresh={() => {
          if (isAutoRefund) {
            fetchRefundVouchers(currentPage, pageSize);
            return;
          }
          fetchVouchers(currentPage, pageSize);
        }}
        emptyMessage={t('messages.noVouchers')}

      />
    </PageLayout>
  );
};
